import { Box, Flex, Grid, GridItem, Text, Tooltip } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppFileDragAndDropZone from "@/features/invoices/app-file-drag-and-drop-zone";
import SVG from "react-inlinesvg";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { FormikProps } from "formik";
import { getFileNameWithoutExtension, isExcel, isPDF } from "@/features/types";
import useCreateStandardOrderForm from "@/features/line-sheet-sets/hooks/use-create-standard-order-form";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { NegativeSequencer } from "@/features/ui/utils/negative-sequencer";
import {
  AttachmentFormField,
  isLineSheetFormField,
  LineSheetFormField,
  LineSheetSetFormFields
} from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page";
import useLineSheetableGrid from "@/features/line-sheet-sets/detail/hooks/use-line-sheetable-grid";
import UploadOrderSheetsModal from "@/features/order-sheet-sets/upload-order-sheets-modal";
import BoutiqueOrderTotalExportModal from "@/features/line-sheet-sets/detail/boutique-order-total-export-modal";
import { KeyedMutator } from "swr";
import { GetDenseLineSheetSetResponse } from "@/features/line-sheet-sets/hooks/use-get-dense-line-sheet-set";
import useIdentity from "@/features/ui/hooks/use-identity";
import UpdateOrderSheetToBeSubmittedModal
  from "@/features/order-status/modals/update-order-sheet-to-be-submitted-modal";
import useLineSheetableRows from "@/features/line-sheet-sets/detail/hooks/use-line-sheetable-rows";
import useFillOutLineSheetSetForm from "@/features/line-sheet-sets/detail/hooks/use-fill-out-line-sheet-set-form";
import { isAttachmentRow, isLineSheetRow } from "@/features/line-sheet-sets/detail/utils/line-sheetable-mapper";
import LineSheetableGrid from "@/features/line-sheet-sets/detail/line-sheetable-grid";
import { IsolatedLineSheetRow } from "@/features/line-sheet-sets/detail/isolated-sheet.type";
import AddOrderConfirmationInvoiceModal from "@/shared/add-order-confimation-invoice-modal";
import IsolatedProformaInvoicePanel
  from "@/features/invoices/order-confirmation-invoices/isolated-proforma-invoice-panel";
import AddProformaInvoiceModal from "@/shared/add-proforma-invoice-modal";
import AddCreditInvoiceModal from "@/shared/add-credit-invoice-modal";
import EditCreditInvoiceModal from "@/shared/edit-credit-invoice-modal";
import IsolatedOrderConfirmationInvoicePanel
  from "@/features/invoices/proforma-invoices/isolated-order-confirmation-invoice-panel";
import AddBankRemittanceReceiptModal from "@/shared/add-bank-remittance-receipt-modal";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import AddProformaInvoiceModalSeason from "@/shared/add-proforma-invoice-modal-season";
import { usePersistedQuery } from "@/features/order-sheet-sets/hooks/use-persisted-query";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";
import EditProformaInvoiceModal from "@/shared/edit-proforma-invoice-modal";
import EditOrderConfirmationInvoiceModal from "@/shared/edit-order-confirmation-invoice-modal";
import AddTransportationModal from "@/shared/add-transportation-modal";
import EditTransportationModal from "@/shared/edit-transportation-modal";
import IsolatedTransportationPanel from "@/features/transportations/isolated-transportation-panel";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import useListProformaInvoices, {
  ListProformaInvoicesPathParameter,
  ListProformaInvoicesRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-list-proforma-invoices";
import OrderHistoryInquiryContent
  from "@/features/my-order/features/order-history/components/inquiry-section/OrderHistoryInquiryContent";
import {
  canManageRightPanelItems,
  getRightPanelTabs,
  resolveRightPanelTab,
  RightPanelTab,
} from "@/features/line-sheet-sets/detail/utils/right-panel-tabs";
import BoutiqueProformaInvoiceSummaryModal from "@/features/line-sheet-sets/detail/boutique-proforma-invoice-summary-modal";
import BoutiqueTransportationSummaryModal from "@/features/line-sheet-sets/detail/boutique-transportation-summary-modal";
import InquiryUnreadBadge
  from "@/features/my-order/features/order-history/components/inquiry-section/InquiryUnreadBadge";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import { CreditInvoice } from "@/features/invoices/credit-invoices/credit-invoice.type";
import { fileIconsByType } from "@/shared/constants";

interface LineSheetableListFormControlProps {
  isEditing?: boolean;
  isRefreshing: boolean;
  formik: FormikProps<LineSheetSetFormFields>;
  mutate?: KeyedMutator<GetDenseLineSheetSetResponse>;
}

export default function LineSheetableListFormControl({
  isEditing,
  isRefreshing,
  formik,
  mutate,
}: LineSheetableListFormControlProps) {
  const identity = useIdentity();
  const { error: showError } = useAppToasts({ id: "GLOBAL" });
  const { tTitle, tCurrencyString, tLocalDateString } = useI18nHelper();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const orderSheetSet = formik.values.orderSheetSet;
  const negativeSequencer = useMemo(() => {
    return new NegativeSequencer();
  }, []);
  type SelectedTab = RightPanelTab;
  const { value: tabQuery, patch: patchTabQuery } = usePersistedQuery<{ tab: SelectedTab }>({
    entries: {
      tab: {
        parse: (raw) => StringTransformer.parseString(raw) as SelectedTab | undefined,
        stringify: StringTransformer.stringifyString,
      },
    },
  });
  // 오더를 볼 때 오른쪽 영역의 탭 — 에이전시와 부티크가 같은 구성을 쓴다(부티크는 오더컨펌·크레딧/디파짓 제외).
  const rightPanelTabs = useMemo(
    () =>
      orderSheetSet
        ? getRightPanelTabs(identity?.company?.type, formik.values.type === "SEASON")
        : [],
    [orderSheetSet, identity?.company?.type, formik.values.type]
  );
  const selectedTab: SelectedTab = resolveRightPanelTab(tabQuery.tab, rightPanelTabs);
  const setSelectedTab = (tab: SelectedTab) => patchTabQuery("tab", tab);
  // 프로포마·운송의 추가/수정은 에이전시만 — 부티크는 목록을 보고, 누르면 요약 모달(상세 페이지로 가지 않는다).
  const canManageItems = canManageRightPanelItems(identity?.company?.type);
  const isBoutiqueViewer = identity?.company?.type === "BOUTIQUE";
  const [viewingProformaInvoiceId, setViewingProformaInvoiceId] = React.useState<number | undefined>(undefined);
  const [viewingTransportationId, setViewingTransportationId] = React.useState<number | undefined>(undefined);

  const [rows, setRows, rowMapper] = useLineSheetableRows({ formik });
  const [isAddOrderConfirmationInvoiceOpen, setIsAddOrderConfirmationInvoiceOpen] = React.useState(false);
  const [isAddProformaInvoiceOpen, setIsAddProformaInvoiceOpen] = React.useState(false);
  const [isAddBankRemittanceReceiptOpen, setIsAddBankRemittanceReceiptOpen] = React.useState(false);
  const [editingProformaInvoiceId, setEditingProformaInvoiceId] = React.useState<number | undefined>(undefined);
  const [editingOrderConfirmationInvoiceId, setEditingOrderConfirmationInvoiceId] = React.useState<number | undefined>(undefined);
  const [isAddTransportationOpen, setIsAddTransportationOpen] = React.useState(false);
  const [isIssueFixingCreditOpen, setIsIssueFixingCreditOpen] = React.useState(false);
  const [viewingCreditInvoiceId, setViewingCreditInvoiceId] = React.useState<number | undefined>(undefined);
  const [editingTransportationId, setEditingTransportationId] = React.useState<number | undefined>(undefined);
  const [payment, setPayment] = React.useState<LightPayment>();
  const rowLengthRef = useRef<number>(rows?.length || 0);
  const [runMutation, setRunMutation] = React.useState(0);

  const orderConfirmationInvoiceIds = useMemo(() => {
    return orderSheetSet?.orderConfirmationInvoices?.map((orderConfirmationInvoice) => {
      return orderConfirmationInvoice.id;
    }) || [];
  }, [orderSheetSet]);

  const proformaInvoiceIds = useMemo(() => {
    return orderSheetSet?.proformaInvoices?.map((proformaInvoice) => {
      return proformaInvoice.id;
    }) || [];
  }, [orderSheetSet]);

  const listProformaInvoicesKey = useMemo(():
    | { parameter: ListProformaInvoicesPathParameter; request: ListProformaInvoicesRequest }
    | undefined => {
    if (identity?.company?.type && proformaInvoiceIds.length > 0) {
      return {
        parameter: { by: identity.company.type, companyId: identity.company.id },
        request: { id__in: proformaInvoiceIds, pageNumber: 1, pageSize: proformaInvoiceIds.length },
      };
    }
    return undefined;
  }, [identity, proformaInvoiceIds]);

  const { data: listProformaInvoicesData, mutate: mutateListProformaInvoices } = useListProformaInvoices(
    listProformaInvoicesKey?.parameter,
    listProformaInvoicesKey?.request,
    { revalidateOnFocus: false }
  );

  const listedProformaInvoices = useMemo(
    () => listProformaInvoicesData?.proformaInvoices ?? [],
    [listProformaInvoicesData]
  );

  // 하자 크레딧의 원인이 될 수 있는 프로포르마 — 취소·대체된 것은 뺀다(백엔드도 같은 기준으로 건너뛴다).
  const fixingCreditCandidates = useMemo(
    () =>
      listedProformaInvoices.filter(
        (p) => p.status !== "CANCELED" && p.status !== "REPLACED"
      ),
    [listedProformaInvoices]
  );

  // 크레딧/디파짓 탭은 크레딧 단위로 평면화해 표시.
  // 각 항목은 어떤 프로포르마에서 사용/적립되었는지 정보를 함께 가진다.
  const flatCreditEntries = useMemo(() => {
    const used: {
      proformaId: number;
      proformaName: string;
      credit: NonNullable<
        (typeof listedProformaInvoices)[number]["usedCreditInvoices"]
      >[number];
    }[] = [];
    const earned: typeof used = [];
    listedProformaInvoices.forEach((p) => {
      (p.usedCreditInvoices ?? []).forEach((credit) =>
        used.push({ proformaId: p.id, proformaName: p.name, credit })
      );
      (p.earnedCreditInvoices ?? []).forEach((credit) =>
        earned.push({ proformaId: p.id, proformaName: p.name, credit })
      );
    });
    return { used, earned, total: used.length + earned.length };
  }, [listedProformaInvoices]);

  const transportationIds = useMemo(() => {
    return orderSheetSet?.transportations?.map((transportation) => {
      return transportation.id;
    }) || [];
  }, [orderSheetSet]);

  const lineSheetableGrid = useLineSheetableGrid({
    orderSheetSet: orderSheetSet,
    lineSheetSet: {
      id: formik.values.id!,
      type: formik.values.type,
      status: formik.values.status!,
      publishedAt: formik.values.publishedAt,
      issuedByBoutique: formik.values.issuedByBoutique!,
      isDeleted: formik.values.isDeleted,
    },
    mutate,
    rows,
    setRows,
  });

  const fillOutLineSheetSetForm = useFillOutLineSheetSetForm({ formik });

  useEffect(() => {
    if (rows && rows.length > 0 && rowLengthRef.current === 0) {
      const lineSheetRow = rows.find(
        (row): row is IsolatedLineSheetRow =>
          isLineSheetRow(row) && row.type === "LINE_SHEET"
      );
      if (lineSheetRow) {
        fillOutLineSheetSetForm(lineSheetRow);
      }
    }
    rowLengthRef.current = rows?.length || 0;
  }, [fillOutLineSheetSetForm, rows]);

  const {
    fire: fireCreateStandardOrderForm,
    data: createStandardOrderFormData,
    error: createStandardOrderFormError,
    isLoading: isCreateStandardOrderFormLoading,
    clear: clearCreateStandardOrderForm,
  } = useCreateStandardOrderForm();

  useEffect(() => {
    if (createStandardOrderFormError) {
      showError(createStandardOrderFormError);
    }
  }, [showError, createStandardOrderFormError]);

  useEffect(() => {
    if (createStandardOrderFormData) {
      //추가하고
      setRows((prev) => {
        const file = createStandardOrderFormData;
        const orderFormRow: LineSheetFormField = {
          id: negativeSequencer.next(),
          file: file,
          name: getFileNameWithoutExtension(file),
          type: "ORDER_FORM",
          compressionStatus: null,
          isCheckable: true,
          isChecked: false,
          orderSheets: [],
          reviewStatus: null,
          updatedAt: new Date(),
          isAllProductsHaveBrand: null,
          brands: [],
          seasons: [],
          unoFormFile: null,
          currency: null,
        };
        const next = [
          ...(prev || []),
          ...(rowMapper.toRows([orderFormRow]) || []),
        ];
        return next;
      });
      clearCreateStandardOrderForm();
    }
  }, [
    createStandardOrderFormData,
    clearCreateStandardOrderForm,
    formik,
    negativeSequencer,
    setRows,
    rowMapper,
  ]);

  const handleFilesAdd = useCallback(
    (value: File | File[]) => {
      const files = Array.isArray(value) ? value : [value];

      setRows((prev) => {
        return [
          ...(prev || []),
          ...(rowMapper.toRows(
            files.map((file) => {
              if (isExcel(file) || isPDF(file)) {
                const next: LineSheetFormField = {
                  id: negativeSequencer.next(),
                  file: file,
                  name: getFileNameWithoutExtension(file),
                  type: "LINE_SHEET",
                  compressionStatus: null,
                  isCheckable: true,
                  isChecked: false,
                  orderSheets: [],
                  reviewStatus: null,
                  updatedAt: new Date(),
                  brands: [],
                  seasons: [],
                  unoFormFile: null,
                  isAllProductsHaveBrand: null,
                  currency: null,
                };
                return next;
              } else {
                const next: AttachmentFormField = {
                  id: negativeSequencer.next(),
                  file: file,
                  name: file.name,
                  isCheckable: true,
                  isChecked: false,
                };
                return next;
              }
            })
          ) || []),
        ];
      });
    },
    [negativeSequencer, rowMapper, setRows]
  );

  return (
    <>
      <Flex
        // overflowY={"auto"}
        // height={"1200px"}
        // style={{ scrollbarGutter: "stable" }}
        flexDirection={"column"}
        fontSize={"12px"}
        gap={"16px"}
      >
        <input
          type={"file"}
          multiple={true}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            // handleFilesChange(e.target.files);
            // e.target.value = "";
          }}
        />
        <Flex flexDirection={"column"}>
          {
            rightPanelTabs.length > 0 &&
            <Flex gap={"12px"} borderBottom={"0.5px solid #D4D4D1"} marginBottom={"8px"}>
              <Text
                style={selectedTab === "LINE_SHEET" ? {
                  borderBottom: "1px solid black",
                  paddingBottom: "8px",
                  fontWeight: "700",
                  cursor: "pointer",
                } : {
                  cursor: "pointer"
                }}
                onClick={() => setSelectedTab("LINE_SHEET")}
              >라인시트</Text>
              { rightPanelTabs.includes("ORDER_CONFIRMATION") &&
                <Text
                  style={selectedTab === "ORDER_CONFIRMATION" ? {
                    borderBottom: "1px solid black",
                    paddingBottom: "8px",
                    fontWeight: "700",
                    cursor: "pointer",
                  } : {
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedTab("ORDER_CONFIRMATION")}
                >{`오더컨펌(${orderConfirmationInvoiceIds.length})`}</Text>
              }

              <Text
                style={selectedTab === "PROFORMA" ? {
                  borderBottom: "1px solid black",
                  paddingBottom: "8px",
                  fontWeight: "700",
                  cursor: "pointer",
                } : {
                  cursor: "pointer"
                }}
                onClick={() => setSelectedTab("PROFORMA")}
              >{`프로프르마(${proformaInvoiceIds.length})`}</Text>
              <Text
                style={selectedTab === "TRANSPORTATION" ? {
                  borderBottom: "1px solid black",
                  paddingBottom: "8px",
                  fontWeight: "700",
                  cursor: "pointer",
                } : {
                  cursor: "pointer"
                }}
                onClick={() => setSelectedTab("TRANSPORTATION")}
              >{`운송/통관(${transportationIds.length})`}</Text>
              { rightPanelTabs.includes("BALANCE") &&
                <Text
                  style={selectedTab === "BALANCE" ? {
                    borderBottom: "1px solid black",
                    paddingBottom: "8px",
                    fontWeight: "700",
                    cursor: "pointer",
                  } : {
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedTab("BALANCE")}
                >{`크레딧/디파짓(${flatCreditEntries.total})`}</Text>
              }
              <Text
                style={selectedTab === "INQUIRY" ? {
                  borderBottom: "1px solid black",
                  paddingBottom: "8px",
                  fontWeight: "700",
                  cursor: "pointer",
                } : {
                  cursor: "pointer"
                }}
                onClick={() => setSelectedTab("INQUIRY")}
              >
                문의
                {orderSheetSet?.id && (
                  <InquiryUnreadBadge orderSheetSetId={orderSheetSet.id} />
                )}
              </Text>
            </Flex>
          }
          <Flex flexDirection={"column"}>
            {isEditing && (
              <>
                <AppFileDragAndDropZone
                  isMultiple={true}
                  onFilesAdd={handleFilesAdd}
                />
                {identity?.company?.type === "AGENCY" && (
                  <Flex gap={"8px"} marginY={"4px"}>
                    <Box flexGrow={1}>
                      <AppButton
                        width={"100%"}
                        isDisabled={(() => {
                          return (
                            formik.values.lineSheetOrAttachmentList.filter(
                              isLineSheetFormField
                            ).length > 0
                          );
                        })()}
                        isLoading={isCreateStandardOrderFormLoading}
                        onClick={() => {
                          fireCreateStandardOrderForm({}, {});
                        }}
                      >
                        <SVG
                          src={"/icons/icon_add.svg"}
                          width={"14px"}
                          height={"14px"}
                          style={{ marginRight: "8px" }}
                        />
                        {tTitle("line_sheet_sets:add_standard_order_form")}
                      </AppButton>
                    </Box>
                    {selectedTab === "LINE_SHEET" && (
                      <AppButton
                        variant={"white"}
                        chakraProps={{ paddingX: "24px" }}
                        isDisabled={!rows?.some((row) => row.isChecked)}
                        onClick={() => {
                          setRows((prev) =>
                            prev?.filter((row) => !row.isChecked)
                          );
                        }}
                      >
                        {tTitle("common:delete_selected")}
                      </AppButton>
                    )}
                  </Flex>
                )}
              </>
            )}
            {selectedTab === "LINE_SHEET" && (
              <LineSheetableGrid
                rows={
                  isEditing
                    ? rows
                    : [...(rows?.filter((row) => !isAttachmentRow(row)) ?? [])]
                }
                setRows={setRows}
                isRefreshing={isRefreshing}
                fileInputRef={fileInputRef}
                sequencer={negativeSequencer}
                lineSheetableGrid={lineSheetableGrid}
                orderSheetSet={orderSheetSet}
                isEditing={isEditing}
              />
            )}
            {
              // OC 목록은 오더 협의중부터 보인다(추가 버튼은 아래에서 서버가 받는 상태만)
              selectedTab === "ORDER_CONFIRMATION" && orderSheetSet?.status && [
                "ORDERED_TO_BOUTIQUE",
                "BOUTIQUE_CONFIRMED",
                "COMPLETED",
                "BUYER_CONFIRMED",
                "TRANSPORTATION_ING",
                "TRANSPORTATION_COMPLETED"].includes(orderSheetSet?.status) && (
                <>
                  {orderConfirmationInvoiceIds.length > 0 && (
                    <IsolatedOrderConfirmationInvoicePanel
                      orderConfirmationInvoiceIds={orderConfirmationInvoiceIds}
                      title={""}
                      isCompact={true}
                      showNoRows={false}
                      setIsAddBankRemittanceReceiptOpen={setIsAddBankRemittanceReceiptOpen}
                      setPayment={setPayment}
                      runMutation={runMutation}
                      onEdit={
                        canManageItems
                          ? (id) => setEditingOrderConfirmationInvoiceId(id)
                          : undefined
                      }
                    />
                  )}
                  {/* OC 업로드는 서버가 받는 상태(오더 협의중·부티크 컨펌·바이어 컨펌)에서만 — 목록은 그 뒤 상태에서도 보인다 */}
                  {["ORDERED_TO_BOUTIQUE", "BOUTIQUE_CONFIRMED", "BUYER_CONFIRMED"].includes(
                    orderSheetSet.status
                  ) && (
                  <Flex flexDirection={"column"} width={"100%"} paddingX={"16px"}>
                    <Box
                      height={"54px"}
                      onClick={() => setIsAddOrderConfirmationInvoiceOpen(true)}
                      display={"flex"}
                      border={"0.5px solid #8F8F8C"}
                      borderRadius={"8px"}
                      boxShadow={"1px 2px 4px 0px rgba(0, 0, 0, 0.10)"}
                      backgroundColor={"white"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      cursor={"pointer"}
                      gap={"4px"}
                    >
                      <SVG
                        src={"/icons-v1_1/plus.svg"}
                        width={"20px"}
                        height={"20px"}
                        // color={"#D4D4D1"}
                      />
                      <Text>
                        추가
                      </Text>
                    </Box>
                  </Flex>
                  )}
                </>
              )
            }
            {
              selectedTab === "PROFORMA" && orderSheetSet?.status && [
              "ORDERED_TO_BOUTIQUE",
              "BOUTIQUE_CONFIRMED",
              "COMPLETED",
              "BUYER_CONFIRMED",
              "TRANSPORTATION_ING",
              "TRANSPORTATION_COMPLETED"].includes(orderSheetSet?.status) && (
                <>
                  {proformaInvoiceIds.length > 0 && (
                    <IsolatedProformaInvoicePanel
                      title=""
                      proformaInvoiceIds={proformaInvoiceIds}
                      isCompact={true}
                      showNoRows={false}
                      setIsAddBankRemittanceReceiptOpen={setIsAddBankRemittanceReceiptOpen}
                      setPayment={setPayment}
                      runMutation={runMutation}
                      onEdit={
                        canManageItems
                          ? (id) => setEditingProformaInvoiceId(id)
                          : isBoutiqueViewer
                          ? (id) => setViewingProformaInvoiceId(id)
                          : undefined
                      }
                    />
                  )}
                  {canManageItems && (
                  <Flex flexDirection={"column"} width={"100%"} paddingX={"16px"}>
                    <Box
                      height={"54px"}
                      onClick={() => setIsAddProformaInvoiceOpen(true)}
                      display={"flex"}
                      border={"0.5px solid #8F8F8C"}
                      borderRadius={"8px"}
                      boxShadow={"1px 2px 4px 0px rgba(0, 0, 0, 0.10)"}
                      backgroundColor={"white"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      cursor={"pointer"}
                      gap={"4px"}
                      opacity={1}
                    >
                      <SVG
                        src={"/icons-v1_1/plus.svg"}
                        width={"20px"}
                        height={"20px"}
                      />
                      <Text>
                        추가
                      </Text>
                    </Box>
                  </Flex>
                  )}
                </>
              )
            }
            {
              selectedTab === "BALANCE" && (
                <Flex flexDirection={"column"} width={"100%"} gap={"16px"}>
                  {/* 크레딧 발행 — 이 오더의 프로포르마를 원인으로 연결한 하자 크레딧을 만든다(에이전시만). */}
                  {canManageItems && (
                    <Tooltip
                      isDisabled={fixingCreditCandidates.length > 0}
                      label={tTitle("proforma_invoices") + " 1개 이상 필요합니다."}
                      placement={"top"}
                    >
                      <Box width={"fit-content"}>
                        <AppButton
                          variant={"white"}
                          isDisabled={fixingCreditCandidates.length === 0}
                          onClick={() => setIsIssueFixingCreditOpen(true)}
                        >
                          <SVG
                            src={"/icons/icon_add.svg"}
                            width={"14px"}
                            height={"14px"}
                            style={{ marginRight: "8px" }}
                          />
                          {tTitle("issue_credit")}
                        </AppButton>
                      </Box>
                    </Tooltip>
                  )}
                  {flatCreditEntries.total === 0 && (
                    <Text color={"#8F8F8C"} fontSize={"12px"}>
                      연결된 크레딧이 없습니다.
                    </Text>
                  )}
                  {/* 사용한 크레딧 */}
                  {flatCreditEntries.used.length > 0 && (
                    <CreditEntrySection
                      title={`사용한 크레딧 (${flatCreditEntries.used.length})`}
                      entries={flatCreditEntries.used}
                      sourceColumnHeader={"사용 프로포르마"}
                      tCurrencyString={tCurrencyString}
                      tLocalDateString={tLocalDateString}
                      onSelect={canManageItems ? setViewingCreditInvoiceId : undefined}
                    />
                  )}
                  {/* 적립된 크레딧 */}
                  {flatCreditEntries.earned.length > 0 && (
                    <CreditEntrySection
                      title={`적립된 크레딧 (${flatCreditEntries.earned.length})`}
                      entries={flatCreditEntries.earned}
                      sourceColumnHeader={"대상 프로포르마"}
                      tCurrencyString={tCurrencyString}
                      tLocalDateString={tLocalDateString}
                      onSelect={canManageItems ? setViewingCreditInvoiceId : undefined}
                    />
                  )}
                </Flex>
              )
            }
            {
              selectedTab === "TRANSPORTATION" && (
                <>
                  {transportationIds.length > 0 && (
                    <IsolatedTransportationPanel
                      transportationIds={transportationIds}
                      onEdit={
                        canManageItems
                          ? (id) => setEditingTransportationId(id)
                          : isBoutiqueViewer
                          ? (id) => setViewingTransportationId(id)
                          : undefined
                      }
                    />
                  )}
                  {canManageItems && (
                  <Flex flexDirection={"column"} width={"100%"} paddingX={"16px"}>
                  <Tooltip
                    isDisabled={proformaInvoiceIds.length > 0}
                    label={tTitle("proforma_invoices") + " 1개 이상 필요합니다."}
                    placement={"top"}
                  >
                  <Box
                    height={"54px"}
                    onClick={() => proformaInvoiceIds.length > 0 && setIsAddTransportationOpen(true)}
                    display={"flex"}
                    border={"0.5px solid #8F8F8C"}
                    borderRadius={"8px"}
                    boxShadow={"1px 2px 4px 0px rgba(0, 0, 0, 0.10)"}
                    backgroundColor={proformaInvoiceIds.length === 0 ? "#F7F9FA" : "white"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    cursor={proformaInvoiceIds.length === 0 ? "not-allowed" : "pointer"}
                    gap={"4px"}
                    opacity={proformaInvoiceIds.length === 0 ? 0.5 : 1}
                  >
                    <SVG
                      src={"/icons-v1_1/plus.svg"}
                      width={"20px"}
                      height={"20px"}
                    />
                    <Text>
                      추가
                    </Text>
                  </Box>
                  </Tooltip>
                </Flex>
                  )}
                </>
              )
            }
            {
              selectedTab === "INQUIRY" && orderSheetSet?.id && (
                <Flex flexDirection={"column"} width={"100%"} paddingX={"16px"}>
                  <React.Suspense fallback={<CenteredSpinner />}>
                    <OrderHistoryInquiryContent orderSheetSetId={orderSheetSet.id} />
                  </React.Suspense>
                </Flex>
              )
            }
          </Flex>
        </Flex>
      </Flex>
      {isBoutiqueViewer && (
        <BoutiqueProformaInvoiceSummaryModal
          proformaInvoice={listedProformaInvoices.find((p) => p.id === viewingProformaInvoiceId)}
          isOpen={viewingProformaInvoiceId !== undefined}
          onClose={() => setViewingProformaInvoiceId(undefined)}
        />
      )}
      {isBoutiqueViewer && viewingTransportationId !== undefined && (
        <BoutiqueTransportationSummaryModal
          transportationId={viewingTransportationId}
          isOpen={true}
          onClose={() => setViewingTransportationId(undefined)}
        />
      )}
      {lineSheetableGrid.disclosureUploadOrderSheetModal.data && (
        <UploadOrderSheetsModal
          {...lineSheetableGrid.disclosureUploadOrderSheetModal.data}
          isOpen={lineSheetableGrid.disclosureUploadOrderSheetModal.isOpen}
          onClose={lineSheetableGrid.disclosureUploadOrderSheetModal.onClose}
        />
      )}
      {lineSheetableGrid.disclosureUpdateToBeSubmittedModal.data && (
        <UpdateOrderSheetToBeSubmittedModal
          key={`UpdateOrderSheetToBeSubmittedModal_${lineSheetableGrid.disclosureUpdateToBeSubmittedModal.data.orderSheetId}`}
          {...lineSheetableGrid.disclosureUpdateToBeSubmittedModal.data}
          isOpen={lineSheetableGrid.disclosureUpdateToBeSubmittedModal.isOpen}
          onClose={lineSheetableGrid.disclosureUpdateToBeSubmittedModal.onClose}
        />
      )}
      {lineSheetableGrid.disclosureOrderTotalExportModal.data && (
        <BoutiqueOrderTotalExportModal
          {...lineSheetableGrid.disclosureOrderTotalExportModal.data}
          isOpen={lineSheetableGrid.disclosureOrderTotalExportModal.isOpen}
          onClose={lineSheetableGrid.disclosureOrderTotalExportModal.onClose}
        />
      )}
      {
        orderSheetSet && <AddOrderConfirmationInvoiceModal
          isOpen={isAddOrderConfirmationInvoiceOpen}
          onClose={async () => {
            setIsAddOrderConfirmationInvoiceOpen(false);
            // mutate 필요
            if (mutate) {
              await mutate();
            }
          }}
          buyerId={orderSheetSet.createdByCompany.id}
          boutiqueId={formik.values.issuedByBoutique?.id!}
          orderSheetSet={orderSheetSet}
          season={formik.values.season}
        />
      }
      {
        orderSheetSet && formik.values.type === "STOCK" && <AddProformaInvoiceModal
          isOpen={isAddProformaInvoiceOpen}
          onClose={async () => {
            setIsAddProformaInvoiceOpen(false);
            // mutate 필요
            if (mutate) {
              await mutate();
            }
          }}
          buyerId={orderSheetSet.createdByCompany.id}
          boutiqueId={formik.values.issuedByBoutique?.id!}
          orderSheetSet={orderSheetSet}
          currency={formik.values.currency}
        />
      }
      {
        orderSheetSet && formik.values.type === "SEASON" && <AddProformaInvoiceModalSeason
          isOpen={isAddProformaInvoiceOpen}
          onClose={async () => {
            setIsAddProformaInvoiceOpen(false);
            // mutate 필요
            if (mutate) {
              await mutate();
            }
          }}
          buyerId={orderSheetSet.createdByCompany.id}
          boutiqueId={formik.values.issuedByBoutique?.id!}
          orderSheetSet={orderSheetSet}
          currency={formik.values.currency}
          season={formik.values.season}
        />
      }
      {
        orderSheetSet && <AddBankRemittanceReceiptModal
          isOpen={isAddBankRemittanceReceiptOpen}
          onClose={() => {
            setIsAddBankRemittanceReceiptOpen(false);
          }}
          payment={payment!}
        />
      }
      {isIssueFixingCreditOpen && fixingCreditCandidates.length > 0 && (
        <AddCreditInvoiceModal
          isOpen={true}
          onClose={() => setIsIssueFixingCreditOpen(false)}
          title={tTitle("issue_credit")}
          defaultType={"FIXING"}
          // 발행사·수입사·에이전시는 프로포르마와 같게(프로포마 상세의 크레딧 추가와 같은 기준).
          orderedBy={fixingCreditCandidates[0].issuedToCompany}
          issuedBy={
            fixingCreditCandidates[0].originallyIssuedByCompany ||
            fixingCreditCandidates[0].issuedByCompany
          }
          issuedThrough={fixingCreditCandidates[0].issuedThroughCompany}
          earningProformaInvoiceCandidates={fixingCreditCandidates}
          onSuccess={() => {
            // 적립된 크레딧 목록은 프로포르마의 earnedCreditInvoices 에서 오므로 그 목록을 다시 읽는다.
            mutateListProformaInvoices();
          }}
        />
      )}
      {viewingCreditInvoiceId && (
        <EditCreditInvoiceModal
          key={viewingCreditInvoiceId}
          creditInvoiceId={viewingCreditInvoiceId}
          isOpen={true}
          proformaInvoices={listedProformaInvoices}
          onClose={() => setViewingCreditInvoiceId(undefined)}
          onSuccess={() => {
            // 탭의 크레딧 목록은 프로포르마의 used/earnedCreditInvoices 에서 오므로 그 목록을 다시 읽는다.
            mutateListProformaInvoices();
          }}
        />
      )}
      {editingProformaInvoiceId && (
        <EditProformaInvoiceModal
          key={editingProformaInvoiceId}
          proformaInvoiceId={editingProformaInvoiceId}
          isOpen={true}
          onClose={() => setEditingProformaInvoiceId(undefined)}
          onSuccess={() => setRunMutation((prev) => prev + 1)}
        />
      )}
      {editingOrderConfirmationInvoiceId && (
        <EditOrderConfirmationInvoiceModal
          key={editingOrderConfirmationInvoiceId}
          orderConfirmationInvoiceId={editingOrderConfirmationInvoiceId}
          isOpen={true}
          onClose={() => setEditingOrderConfirmationInvoiceId(undefined)}
          onSuccess={() => setRunMutation((prev) => prev + 1)}
        />
      )}
      {editingTransportationId && (
        <EditTransportationModal
          key={editingTransportationId}
          transportationId={editingTransportationId}
          isOpen={true}
          onClose={() => setEditingTransportationId(undefined)}
          onSuccess={async () => {
            if (mutate) {
              await mutate();
            }
          }}
        />
      )}
      <AddTransportationModal
        isOpen={isAddTransportationOpen}
        onClose={() => setIsAddTransportationOpen(false)}
        shippedTo={orderSheetSet?.createdByCompany as any}
        shippedFromList={
          formik.values.issuedByBoutique
            ? [formik.values.issuedByBoutique as any]
            : undefined
        }
        proformaInvoices={formik.values.type === "STOCK" ? listedProformaInvoices.filter((p) => p.status === "NORMAL") : undefined}
        onSuccess={async () => {
          if (mutate) {
            await mutate();
          }
        }}
      />

    </>
  );
}

// 크레딧/디파짓 탭 — 프로포르마(AppFlatCard)의 시각 톤과 통일.
//   · 헤더: 1줄짜리 흰 카드(box-shadow + 8px radius + 0.5px #8F8F8C border)
//   · 데이터 행: 각자 별개 카드(동일 톤). hover 시 파랑 border + 옅은 회색 배경 + 그림자 강화.
interface CreditEntry {
  proformaId: number;
  proformaName: string;
  credit: CreditInvoice;
}

// 그리드 컬럼: [24px 인디케이터 슬롯(빈)] | 번호 | 발행 사유 | 시즌 | 발행일 | 출처 프로포르마 | 금액 | 파일
// 24px 슬롯은 프로포르마(AppGrid)의 인디케이터 폭과 동일 — 헤더/데이터 행 정렬을 위함.
const CREDIT_GRID_TEMPLATE_COLUMNS =
  "16px 64px minmax(160px, 2fr) 56px 88px minmax(120px, 1fr) minmax(120px, 140px) 56px";

function CreditEntrySection({
  title,
  entries,
  sourceColumnHeader,
  tCurrencyString,
  tLocalDateString,
  onSelect,
}: {
  title: string;
  entries: CreditEntry[];
  sourceColumnHeader: string;
  tCurrencyString: ReturnType<typeof useI18nHelper>["tCurrencyString"];
  tLocalDateString: ReturnType<typeof useI18nHelper>["tLocalDateString"];
  /** 행을 눌렀을 때(크레딧 id). 없으면 행은 보기만 한다. */
  onSelect?: (creditInvoiceId: number) => void;
}) {
  return (
    <Box>
      <Text
        textStyle={"body-sm-bold"}
        color={"#444444"}
        marginBottom={"8px"}
      >
        {title}
      </Text>
      {/* 헤더 — 프로포르마 그리드 헤더와 동일 톤 */}
      <Flex
        minHeight={"36px"}
        boxShadow={"0px 2px 4px 0px rgba(0, 0, 0, 0.10)"}
        border={"0.5px solid #8F8F8C"}
        borderRadius={"8px"}
        background={"#FFF"}
        padding={"0 16px"}
        alignItems={"center"}
        marginBottom={"12px"}
      >
        <Grid
          flexGrow={1}
          templateColumns={CREDIT_GRID_TEMPLATE_COLUMNS}
          gap={"8px"}
          padding={"0 8px"}
          fontSize={"12px"}
          fontWeight={700}
          color={"#444440"}
          alignItems={"center"}
        >
          {/* 인디케이터 슬롯(빈) — 데이터 행과 컬럼 정렬을 맞춤 */}
          <GridItem />
          <GridItem>번호</GridItem>
          <GridItem>발행 사유</GridItem>
          <GridItem>시즌</GridItem>
          <GridItem>발행일</GridItem>
          <GridItem>{sourceColumnHeader}</GridItem>
          <GridItem textAlign={"right"}>금액</GridItem>
          <GridItem textAlign={"center"}>파일</GridItem>
        </Grid>
      </Flex>
      {/* 데이터 행 — 각자 카드, 카드 사이 gap 12px */}
      <Flex flexDirection={"column"} gap={"12px"}>
        {entries.map((entry, idx) => (
          <CreditEntryRow
            key={`CreditEntry_${entry.proformaId}_${entry.credit.id}_${idx}`}
            entry={entry}
            tCurrencyString={tCurrencyString}
            tLocalDateString={tLocalDateString}
            onSelect={onSelect}
          />
        ))}
      </Flex>
    </Box>
  );
}

function CreditEntryRow({
  entry,
  tCurrencyString,
  tLocalDateString,
  onSelect,
}: {
  entry: CreditEntry;
  tCurrencyString: ReturnType<typeof useI18nHelper>["tCurrencyString"];
  tLocalDateString: ReturnType<typeof useI18nHelper>["tLocalDateString"];
  onSelect?: (creditInvoiceId: number) => void;
}) {
  const { credit, proformaId, proformaName } = entry;
  const [isHovered, setIsHovered] = useState(false);
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const fileType = credit.file?.name
    ? credit.file.name.toLowerCase().split(".").at(-1)?.toLowerCase() ??
      "default"
    : "default";

  return (
    <Flex
      minHeight={"48px"}
      boxShadow={
        isHovered
          ? "0px 2px 4px 0px rgba(0, 0, 0, 0.20)"
          : "0px 2px 4px 0px rgba(0, 0, 0, 0.10)"
      }
      border={isHovered ? "0.5px solid #1272EF" : "0.5px solid #8F8F8C"}
      borderRadius={"8px"}
      background={isHovered ? "#F7F9FA" : "#FFF"}
      padding={"8px"}
      marginLeft={"16px"}
      marginRight={"16px"}
      alignItems={"center"}
      fontSize={"12px"}
      color={"#444440"}
      cursor={onSelect ? "pointer" : "default"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect?.(credit.id)}
    >
      <Grid
        flexGrow={1}
        templateColumns={CREDIT_GRID_TEMPLATE_COLUMNS}
        gap={"8px"}
        alignItems={"center"}
      >
        {/* 인디케이터 슬롯(빈) — 헤더와 컬럼 정렬을 맞춤. 프로포르마(AppGrid)와 동일한 24px 폭. */}
        <GridItem />
        <GridItem overflow={"hidden"} textOverflow={"ellipsis"} whiteSpace={"nowrap"}>
          {credit.number || `#${credit.id}`}
        </GridItem>
        <GridItem
          minWidth={0}
          color={"#6F6F6C"}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          whiteSpace={"nowrap"}
          title={credit.publicComment || undefined}
        >
          {credit.publicComment || "-"}
        </GridItem>
        <GridItem color={"#6F6F6C"}>{credit.season || "-"}</GridItem>
        <GridItem color={"#6F6F6C"}>
          {credit.issuedOn ? tLocalDateString(new Date(credit.issuedOn)) : "-"}
        </GridItem>
        <GridItem
          minWidth={0}
          color={"#6F6F6C"}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          whiteSpace={"nowrap"}
        >
          #{proformaId} {proformaName}
        </GridItem>
        <GridItem textAlign={"right"} fontWeight={700}>
          {credit.amount
            ? tCurrencyString(credit.amount.value, {
                type: "SYMBOL",
                code: credit.amount.currency,
              })
            : "-"}
        </GridItem>
        <GridItem textAlign={"center"}>
          {credit.file ? (
            <Flex
              as={"button"}
              type={"button"}
              aria-label={"download"}
              display={"inline-flex"}
              padding={"2px"}
              borderRadius={"4px"}
              color={"#6F6F6C"}
              alignItems={"center"}
              gap={"2px"}
              _hover={{ color: "#1272EF", background: "#F0F6FF" }}
              onClick={(e) => {
                e.stopPropagation();
                if (credit.file) {
                  fireDownloadUnoFile({ unoFileId: credit.file.id }, {});
                }
              }}
            >
              <SVG
                src={fileIconsByType[fileType] ?? fileIconsByType.default}
                width={"12px"}
                height={"12px"}
              />
              <SVG
                src={"/icons-v1_1/download.svg"}
                width={"12px"}
                height={"12px"}
              />
            </Flex>
          ) : (
            <Text color={"#D4D4D1"}>-</Text>
          )}
        </GridItem>
      </Grid>
    </Flex>
  );
}
